import { useCallback, useEffect, useMemo, useState } from 'react'

import { Alert, Spinner } from 'app/components/design-system-components'
import Badge from 'app/components/design-system-components/Badges'
import Box from 'app/components/design-system-components/Box'
import Flex from 'app/components/design-system-components/Flex'
import { ClearRounded } from 'app/components/design-system-components/icons/content'
import SwitchButton from 'app/components/design-system-components/inputs/Switch'
import LoadingSection from 'app/components/design-system-components/LoadingSection'
import Pagination from 'app/components/design-system-components/Pagination'
import Table from 'app/components/design-system-components/Table'
import * as Display from 'app/components/display/display'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import {
  fetchSupportAccessGrants,
  fetchSupportAccessSetting,
  GrantStatus,
  SupportAccessGrant,
  updateSupportAccessSetting,
} from 'app/sm/team_viewing_preference/supportAccess/api'
import { formatStdTimeStamp } from 'app/utils/datetime/helpers'
import {
  createInitialLoadingState,
  createLoadingStateUtils,
} from 'app/utils/loading-states'

const PAGE_SIZE = 10

interface SupportAccessSettingsProps {
  teamGUID: string
}

type BadgeVariant = 'green' | 'red' | 'gray'

function getStatusBadgeVariant(status: GrantStatus): BadgeVariant {
  const map: Record<GrantStatus, BadgeVariant> = {
    active: 'green',
    revoked: 'red',
    expired: 'gray',
  }
  return map[status]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return formatStdTimeStamp(dateStr)
}

function SupportAccessSettings({ teamGUID }: SupportAccessSettingsProps) {
  const [allowAccess, setAllowAccess] = useState(false)
  const [grants, setGrants] = useState<SupportAccessGrant[]>([])
  const [grantsUnavailable, setGrantsUnavailable] = useState(false)
  const [loadingState, setLoadingState] = useState(createInitialLoadingState())
  const loadingUtils = useMemo(
    () => createLoadingStateUtils(setLoadingState),
    [],
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const loadData = useCallback(() => {
    loadingUtils.startLoading()
    const settingPromise = fetchSupportAccessSetting(teamGUID)
    const grantsPromise = fetchSupportAccessGrants(teamGUID).catch(() => {
      setGrantsUnavailable(true)
      return [] as SupportAccessGrant[]
    })
    Promise.all([settingPromise, grantsPromise])
      .then(([setting, grantsData]) => {
        setAllowAccess(setting.allowSupportAccess)
        setGrants(grantsData || [])
        loadingUtils.markDoneSuccessfully()
      })
      .catch((err) => {
        loadingUtils.setError(err)
      })
  }, [teamGUID, loadingUtils])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Non-optimistic: state only updates after API success, so the toggle
  // never shows a value that hasn't been persisted.
  const handleToggle = useCallback(() => {
    if (saving) return

    const newValue = !allowAccess
    setSaving(true)
    setSaveError('')
    updateSupportAccessSetting(teamGUID, newValue)
      .then(() => {
        setAllowAccess(newValue)
        setSaving(false)
      })
      .catch((err) => {
        setSaveError(
          typeof err?.message === 'string'
            ? err.message
            : 'Failed to update setting',
        )
        setSaving(false)
      })
  }, [teamGUID, saving, allowAccess])

  const columns = useMemo(
    () => [
      { Header: 'User', accessor: 'userEmail' },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }: { value: GrantStatus }) => (
          <Badge variant={getStatusBadgeVariant(value)} text={value} />
        ),
      },
      {
        Header: 'Granted',
        accessor: 'grantedAt',
        Cell: ({ value }: { value: string }) => formatDate(value),
      },
      {
        Header: 'Expires',
        accessor: 'expiresAt',
        Cell: ({ value }: { value: string }) => formatDate(value),
      },
      {
        Header: 'Revoked',
        accessor: 'revokedAt',
        Cell: ({ value }: { value: string | null }) => formatDate(value),
      },
    ],
    [],
  )

  return (
    <Box>
      <LoadingSection
        loadingState={loadingState}
        actionHandler={loadData}
        hideChildrenInitially
      >
        {saveError && (
          <Alert
            variant="errorWithBg"
            mb={4}
            actionIconBtnConfig={{
              onClick: () => setSaveError(''),
              iconCmp: ClearRounded,
            }}
          >
            {saveError}
          </Alert>
        )}

        <Box mb={5}>
          <Display.SectionHeader
            text="Allow Support Access"
            hasSeperator={false}
            textClass="pl0"
            as="div"
          >
            {null}
          </Display.SectionHeader>
          <Display.DefaultBodyText>
            <p>
              When enabled, authorised Snug support staff can request temporary
              read-only access to your team&apos;s data for troubleshooting
              purposes.
            </p>
          </Display.DefaultBodyText>
          <Flex alignItems="center">
            <SwitchButton
              buttonID="support-access-toggle"
              value={allowAccess}
              onChange={handleToggle}
              disabled={saving}
            />
            <Box ml={2}>{allowAccess ? 'Enabled' : 'Disabled'}</Box>
            {saving && (
              <Box ml={3}>
                <Spinner />
              </Box>
            )}
          </Flex>
        </Box>

        {!grantsUnavailable && (
          <Box>
            <Display.SectionHeader
              text="Access History"
              hasSeperator={false}
              textClass="pl0"
              as="div"
            >
              {null}
            </Display.SectionHeader>
            <Display.DefaultBodyText>
              <p>A log of all support access sessions for your team.</p>
            </Display.DefaultBodyText>

            {grants.length === 0 ? (
              <Box>No support access grants found.</Box>
            ) : (
              <>
                <Box width="100%" overflowX="auto">
                  <Table
                    columns={columns}
                    dataToBeEntered={grants.slice(
                      (currentPage - 1) * PAGE_SIZE,
                      currentPage * PAGE_SIZE,
                    )}
                    headerBgColor={theme.colors.white}
                    textColor={theme.colors.gray600}
                    showHeaderBottomBorder
                    showRowBottomBorder
                    thPadding={`${theme.space[5]}px`}
                    fontSize={theme.fontSizes.pRegular14}
                    verticalAlignTop
                  />
                </Box>
                {Math.ceil(grants.length / PAGE_SIZE) > 1 && (
                  <Pagination
                    canNextPage={
                      currentPage < Math.ceil(grants.length / PAGE_SIZE)
                    }
                    canPreviousPage={currentPage > 1}
                    currentPageNum={currentPage}
                    maxPageNumbers={Math.ceil(grants.length / PAGE_SIZE)}
                    onPaginationButtonClicked={(
                      _selectedPage: number,
                      pageNumber: number,
                    ) => setCurrentPage(pageNumber)}
                  />
                )}
              </>
            )}
          </Box>
        )}
      </LoadingSection>
    </Box>
  )
}

export default SupportAccessSettings
